import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Spin,
  Tag,
  Empty,
  Typography,
  Button,
  Space,
  Descriptions,
  Row,
  Col,
} from 'antd';
import {
  ReloadOutlined,
  LoginOutlined,
  LogoutOutlined,
  SettingOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { getRequest, postRequest, ResponseDataType } from 'api';
import { toast } from 'react-toastify';
import styles from './invoiceConfig.module.css';

const { Title } = Typography;

interface SchoolBillConfigResDataType {
  id: number;
  schoolId: number;
  msthue: string;
  account: string;
  password?: string;
  invoiceType: number;
  symbol: string;
  number: number;
  mddkdoanh?: string;
  tddkdoanh?: string;
  dcddkdoanh?: string;
}

interface InvoiceUsageSummary {
  remainingQuantity: number;
  totalPurchased: number;
}

export const InvoiceConfig: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [billConfig, setBillConfig] = useState<SchoolBillConfigResDataType | null>(null);
  const [remainingCount, setRemainingCount] = useState<number | null>(null);

  // Trạng thái kết nối XBill
  const [loginLoading, setLoginLoading] = useState(false);
  const [xBillConnected, setXBillConnected] = useState<boolean>(() => {
    const status = localStorage.getItem('xbill_status') || sessionStorage.getItem('xbill_status');
    const token = localStorage.getItem('xbill_token') || sessionStorage.getItem('xbill_token');
    return status === 'LOGGED_IN' && !!token;
  });
  const [loginTime, setLoginTime] = useState<string | null>(() => {
    return localStorage.getItem('xbill_login_time');
  });

  const schoolIdStr = localStorage.getItem('schoolId');
  const schoolId = schoolIdStr ? parseInt(schoolIdStr, 10) : null;
  const currentYear = new Date().getFullYear();

  // Tải thông tin cấu hình
  const fetchInvoiceConfig = useCallback(async () => {
    if (!schoolId) {
      toast.error('Không tìm thấy thông tin trường học. Vui lòng chọn trường học trước.');
      return;
    }

    setLoading(true);
    try {
      const configRes: ResponseDataType<SchoolBillConfigResDataType> = await getRequest(
        `/cms/school-bill-config?schoolId=${schoolId}`
      );

      if (configRes?.code === 200 && configRes.info) {
        const config = configRes.info;
        setBillConfig(config);

        if (config.symbol) {
          try {
            const summaryRes: ResponseDataType<InvoiceUsageSummary> = await getRequest(
              `/cms/school-bill-config/invoice-usage-summary?schoolId=${schoolId}&year=${currentYear}&invoiceSymbol=${config.symbol}`
            );
            if (summaryRes?.code === 200 && summaryRes.info) {
              setRemainingCount(summaryRes.info.remainingQuantity);
            } else {
              setRemainingCount(config.number || 0);
            }
          } catch {
            setRemainingCount(config.number || 0);
          }
        } else {
          setRemainingCount(config.number || 0);
        }
      } else {
        setBillConfig(null);
        setRemainingCount(null);
      }
    } catch (error) {
      console.error('Lỗi khi tải thông tin cấu hình hóa đơn:', error);
      toast.error('Đã xảy ra lỗi khi tải thông tin cấu hình hóa đơn');
    } finally {
      setLoading(false);
    }
  }, [schoolId, currentYear]);

  useEffect(() => {
    fetchInvoiceConfig();
  }, [fetchInvoiceConfig]);

  // Đăng nhập XBill
  const handleLoginXBill = async () => {
    if (!billConfig?.msthue || !billConfig?.account || !billConfig?.password) {
      toast.error('Trường chưa cấu hình đầy đủ Mã số thuế, Tài khoản hoặc Mật khẩu');
      return;
    }
    setLoginLoading(true);
    try {
      const res: any = await postRequest('/token/2', {
        doanhnghiep_mst: billConfig.msthue,
        username: billConfig.account,
        password: billConfig.password,
      });

      const token = res?.info?.result?.access_token;
      if (token) {
        const timeNow = new Date().toLocaleString('vi-VN');
        localStorage.setItem('xbill_token', token);
        sessionStorage.setItem('xbill_token', token);
        localStorage.setItem('xbill_status', 'LOGGED_IN');
        sessionStorage.setItem('xbill_status', 'LOGGED_IN');
        localStorage.setItem('xbill_login_time', timeNow);
        setXBillConnected(true);
        setLoginTime(timeNow);
        toast.success('Đăng nhập XBill thành công! Phiên kết nối đã sẵn sàng.');
      } else {
        toast.error('Không lấy được token XBill. Vui lòng kiểm tra lại thông tin tài khoản.');
      }
    } catch (err) {
      console.error('XBill login error:', err);
      toast.error('Lỗi khi kết nối tới XBill');
    } finally {
      setLoginLoading(false);
    }
  };

  // Hủy phiên đăng nhập
  const handleLogoutXBill = () => {
    localStorage.removeItem('xbill_token');
    sessionStorage.removeItem('xbill_token');
    localStorage.removeItem('xbill_login_time');
    localStorage.setItem('xbill_status', 'LOGGED_OUT');
    sessionStorage.setItem('xbill_status', 'LOGGED_OUT');
    setXBillConnected(false);
    setLoginTime(null);
    toast.info('Đã đăng xuất khỏi XBill');
  };

  return (
    <div className={styles.container}>
      {/* Header trang */}
      <div className={styles.header_section}>
        <Title level={4} className={styles.page_title}>
          <SettingOutlined style={{ color: '#1677ff' }} />
          Cấu hình hóa đơn
        </Title>
        {billConfig && (
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchInvoiceConfig} loading={loading}>
              Làm mới
            </Button>
          </Space>
        )}
      </div>

      <Spin spinning={loading} tip="Đang tải dữ liệu...">
        <Card className={styles.invoice_config_card} bodyStyle={{ padding: '20px 24px' }}>
          {!loading && !billConfig ? (
            <Empty
              description="Chưa có thông tin cấu hình hóa đơn cho trường này. Vui lòng liên hệ quản trị viên."
              style={{ padding: '40px 0' }}
            />
          ) : (
            <>
              {/* Thanh trạng thái kết nối XBill */}
              <div className={styles.connection_bar}>
                <div className={styles.connection_status_left}>
                  <Tag
                    color={xBillConnected ? 'success' : 'default'}
                    icon={xBillConnected ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}
                  >
                    {xBillConnected ? 'Đã kết nối XBill' : 'Chưa đăng nhập XBill'}
                  </Tag>

                  {xBillConnected && loginTime && (
                    <span className={styles.status_subtext}>
                      (Đăng nhập lúc: {loginTime})
                    </span>
                  )}
                </div>

                <Space>
                  <Button
                    type={xBillConnected ? 'default' : 'primary'}
                    size="small"
                    icon={<LoginOutlined />}
                    loading={loginLoading}
                    onClick={handleLoginXBill}
                  >
                    {xBillConnected ? 'Đăng nhập lại' : 'Đăng nhập XBill'}
                  </Button>

                  {xBillConnected && (
                    <Button danger size="small" icon={<LogoutOutlined />} onClick={handleLogoutXBill}>
                      Đăng xuất
                    </Button>
                  )}
                </Space>
              </div>

              {/* Bố cục 2 cột rõ ràng */}
              <Row gutter={24}>
                {/* Cột 1: Thông tin đơn vị */}
                <Col xs={24} md={12}>
                  <div className={styles.column_title}>Thông tin đơn vị</div>
                  <Descriptions bordered size="middle" column={1}>
                    <Descriptions.Item label="Mã số thuế (MST)">
                      {billConfig?.msthue || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Mã ĐVKD">
                      {billConfig?.mddkdoanh || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Tên ĐVKD">
                      {billConfig?.tddkdoanh || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Địa chỉ ĐVKD">
                      {billConfig?.dcddkdoanh || '-'}
                    </Descriptions.Item>
                  </Descriptions>
                </Col>

                {/* Cột 2: Tài khoản & Hóa đơn XBill */}
                <Col xs={24} md={12}>
                  <div className={styles.column_title}>Cấu hình hóa đơn XBill</div>
                  <Descriptions bordered size="middle" column={1}>
                    <Descriptions.Item label="Tài khoản XBill">
                      {billConfig?.account || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Mật khẩu">
                      {billConfig?.password ? '••••••••' : '-'}</Descriptions.Item>
                    <Descriptions.Item label="Ký hiệu hóa đơn">
                      {billConfig?.symbol || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Loại hoá đơn">
                      {billConfig?.invoiceType === 1
                        ? 'Loại 1: Hóa đơn GTGT'
                        : billConfig?.invoiceType === 2
                          ? 'Loại 2: Hóa đơn Bán hàng'
                          : billConfig?.invoiceType ?? '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Số lượng đăng ký">
                      {billConfig?.number !== undefined ? billConfig.number.toLocaleString() : 0}
                    </Descriptions.Item>
                    <Descriptions.Item label="Số lượng còn lại">
                      <span style={{ color: '#1677ff', fontWeight: 600 }}>
                        {remainingCount !== null ? remainingCount.toLocaleString() : 0}
                      </span>
                    </Descriptions.Item>
                  </Descriptions>
                </Col>
              </Row>
            </>
          )}
        </Card>
      </Spin>
    </div>
  );
};
